import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Flex, useBreakpointValue } from '@chakra-ui/react';
import { Modal, Button } from '@morressier/alembic';
import _ from 'lodash';
import { IFiguresAndTablesFile } from '@publishing-engine/domain/models/submission';
import { generateValidationSchema } from 'src/utils/validation';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { FormFieldFiguresInput } from '@publishing-engine/domain/types/article';
import Figures, {
  IFiguresValue
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/Figures';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import { ISubmissionFieldsResponse } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: IFiguresValue) => void;
  formField: FormFieldFiguresInput;
  value?: IFiguresAndTablesFile;
  submissionContextId: string;
  submissionFields?: ISubmissionFieldsResponse;
}

const FiguresUpdateModal: React.FC<Props> = props => {
  const {
    isOpen,
    onClose,
    onSubmit,
    formField,
    submissionContextId,
    submissionFields
  } = props;
  const modalWidth = useBreakpointValue({ base: '95vw', md: '1055px' });

  const defaultValues = _.set(
    {} as IFiguresValue,
    formField.caseDataFields.main,
    props.value
  );

  const schema = generateValidationSchema({
    figures: formField
  });

  const methods = useForm({
    defaultValues,
    resolver: ajvResolver(schema)
  });

  const validateAndSubmit = (data: IFiguresValue) => {
    if (!validateFigures(data)) {
      scrollToFirstError();
      return;
    }
    onSubmit(data);
    onClose();
  };

  const validateFigures = (data: IFiguresValue): boolean => {
    let isValid = true;

    // Clear any previous errors on figures/tables
    methods.clearErrors('fileSet.Figures');

    const figures = data.fileSet?.Figures;

    // If no figures array → valid
    if (!figures || figures.length === 0) return true;

    // Check if all figures are optional (completely empty)
    const allEmpty = figures.every((fig: any) => {
      const noCaption = !fig.caption || fig.caption.trim() === '';
      const noFile = !fig.file;
      return noCaption && noFile;
    });

    if (allEmpty) {
      // Entire block optional → valid
      return true;
    }

    // Track duplicates (case-insensitive, trimmed)
    const seenCaptions = new Set<string>();

    // Validate each figure
    figures.forEach((fig: any, idx: number) => {
      const captionRaw = fig.caption ?? '';
      const caption = captionRaw.trim();
      // Duplicate caption check: only for non-empty captions
      if (caption !== '') {
        const normalized = caption.toLowerCase();
        if (seenCaptions.has(normalized)) {
          methods.setError(`fileSet.Figures.${idx}.caption`, {
            type: 'manual',
            message: 'Duplicate caption not allowed for figures.'
          });
          isValid = false;
        } else {
          seenCaptions.add(normalized);
        }
      }
    });

    return isValid;
  };

  const checkIsFileLoading = () => {
    const values = methods.getValues();

    return (
      values.fileSet?.Figures?.some(figure => figure.file?.isUploading) ?? false
    );
  };

  const isFileUploading = checkIsFileLoading();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      width={modalWidth}
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(data => {
            validateAndSubmit(data);
          })}
        >
          <Flex
            gap={4}
            bgColor="surfacePrimaryFill"
            flexDirection="column"
            borderRadius="base"
            alignItems="center"
            padding={{ base: 3, md: 4 }}
          >
            <Flex width="100%" alignSelf="center">
              <Figures
                config={formField}
                submissionId={submissionContextId}
                isFromEditModal={true}
                submissionFields={submissionFields}
              />
            </Flex>
            <Flex
              gap={2}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf="stretch"
              flexWrap={{ base: 'wrap', md: 'nowrap' }}
            >
              <Button
                variant="secondary"
                onClick={onClose}
                borderRadius="small"
                disabled={isFileUploading}
              >
                Exit Without Saving
              </Button>
              <Button
                variant="primary"
                type="submit"
                borderRadius="small"
                disabled={isFileUploading}
              >
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default FiguresUpdateModal;
